<script setup lang="ts">
import { type Component, computed } from 'vue';

import { useDogtagStore } from '@/models/dogtag-store';

import type { Clan } from '@/types/vortex';

import Skeleton from '@/components/UI/skeleton.vue';

import type { Size } from '../model/types';
import ConstructorEl from './constructor.vue';
import Emblem from './emblem.vue';

import loaderMaskImage from './images/dogtag-loader-mask.webp';

const { spaId: accountId, size = 'SM' } = defineProps<{
  spaId: number | string;
  size?: Size;
}>();

const { getClanDataByAccountId, useDogtag } = useDogtagStore();

const componentsMap = useDogtag(accountId.toString());

enum DogtagType {
  CONSTRUCTOR = 'constructor',
  EMBLEM = 'emblem',
}

const viewComponentMap = new Map<DogtagType, Component>([
  [DogtagType.CONSTRUCTOR, ConstructorEl],
  [DogtagType.EMBLEM, Emblem],
]);

/**
 * Determines the type of Dogtag to display based on the components map.
 *
 * @returns {DogtagType | null} The Dogtag type
 * - `EMBLEM` if only a patch or emblem component exists
 * - `CONSTRUCTOR` if multiple components or other combinations exist
 * - `null` if the components map is not available or empty
 */
const currentType = computed<DogtagType | null>(() => {
  if (!componentsMap.value) {
    return null;
  }

  const fieldsCount = Object.keys(componentsMap.value).length;
  if (fieldsCount === 0) {
    return null;
  }

  const isOnlyOne = fieldsCount === 1;

  const isPatch = 'patch' in componentsMap.value && isOnlyOne;
  const isEmblem = 'emblem' in componentsMap.value && isOnlyOne;

  if (isEmblem || isPatch) {
    return DogtagType.EMBLEM;
  }

  return DogtagType.CONSTRUCTOR;
});

const viewComponent = computed<Component | undefined>(() =>
  currentType.value ? viewComponentMap.get(currentType.value) : undefined
);

const clanInfo = ref<Clan | null>(null);

const getClanData = async () => {
  clanInfo.value = (await getClanDataByAccountId(accountId)) || null;
};

const isLoading = computed(() => !currentType.value);
</script>

<template>
  <div :class="['dogtag', 'size', size]">
    <transition>
      <skeleton
        v-if="isLoading"
        class="skeleton"
        :style="{ maskImage: `url(${loaderMaskImage})` }"
      />
      <div
        v-else
        :class="['dogtag-wrap', size]"
      >
        <component
          :is="viewComponent"
          :components="componentsMap"
          :clan-info="clanInfo"
          :size="size"
          @get-clan-data="getClanData()"
        />
      </div>
    </transition>
  </div>
</template>

<style scoped lang="postcss">
.size {
  &.XS {
    --size: 24px;
  }

  &.SM {
    --size: 40px;
  }

  &.MD {
    --size: 160px;
  }
}

.dogtag {
  position: relative;
  width: var(--size);
  height: var(--size);
}

.skeleton {
  mask-size: 100%;
  width: 100%;
  height: 100%;
  position: absolute;
}

.dogtag-wrap {
  position: relative;
  padding: 0;

  width: var(--size);
  height: var(--size);

  & > * {
    position: absolute;
  }
}
</style>
